import { cx } from '@emotion/css';
import { ChangeEvent, forwardRef } from 'react';
import { useController } from 'react-hook-form';
import DefaultCheckbox from '@mui/material/Checkbox';

import mergeRefs from '@julofinance/web-helpers/dist/fn/mergeRefs';
import callAllFn from '@julofinance/web-helpers/dist/fn/callAllFn';

import FieldGroup from '../field-group';

import { CheckboxHookFormProps } from './types';
import { checkboxCx } from './styles';

const CheckboxHookForm = forwardRef<HTMLDivElement, CheckboxHookFormProps>(
  (props, ref) => {
    const {
      formContext,
      name,
      rules,
      required,
      defaultValue,
      isInvalid,
      label,
      className,
      helperText,
      onChange,
      onBlur,
      value,
      checkboxClassName,
      checked,
      inputRef = null,
      ...resProps
    } = props;

    const { control } = formContext;
    const {
      fieldState: { error, invalid },
      field: {
        ref: controlRef,
        onChange: controllerOnChange,
        onBlur: controllerOnBlur,
        value: controllerValue,
        ...resFieldProps
      },
    } = useController({
      name,
      control,
      defaultValue,
      rules: {
        ...(required && { required: 'This field is required' }),
        ...(rules && rules),
      },
    });

    const isChecked =
      typeof controllerValue === 'boolean' ? controllerValue : checked;

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
      if (value) {
        return controllerOnChange(e.target.value);
      }

      controllerOnChange(e);
    }

    return (
      <FieldGroup
        ref={ref}
        invalid={isInvalid || invalid}
        error={error}
        helperText={helperText}
        className={className}
        name={name}
      >
        <div className={cx(checkboxCx, 'checkbox-wrapper')}>
          <DefaultCheckbox
            {...resProps}
            checked={isChecked}
            className={checkboxClassName}
            inputRef={mergeRefs(controlRef, inputRef)}
            onChange={callAllFn(handleChange, onChange)}
            onBlur={callAllFn(controllerOnBlur, onBlur)}
            value={value}
            {...resFieldProps}
          />

          {label && (
            <div className='checkbox-label'>
              <span>{label}</span>
            </div>
          )}
        </div>
      </FieldGroup>
    );
  },
);

export default CheckboxHookForm;
